import React, { useEffect, useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { Container, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import PdfPreviewCard from "../Components/PdfPreviewCard";
import { getApiBase } from "../utils/paths";

const getReportTitle = (report) => {
  const rawTitle = report.title || report.pdf_path?.split("/").pop() || "";
  const baseName = rawTitle.replace(/\.pdf$/i, "");
  const normalized = baseName
    .replace(/[-_]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  return normalized || `Annual Report ${report.financial_year || report.year || ""}`;
};

const AnnualReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchReports = async () => {
      setLoading(true);
      try {
        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/public/reports.php?type=annual`);
        if (!response.ok) throw new Error("Unable to load annual reports");
        const data = await response.json();
        if (active) {
          setReports(data.items || []);
        }
      } catch (error) {
        console.error("Failed to load annual reports", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchReports();
    return () => {
      active = false;
    };
  }, []);

  const resolvePdfUrl = (report) => {
    if (!report.id) return "";
    return `${getApiBase()}/public/report-file.php?id=${encodeURIComponent(report.id)}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 18, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 140, damping: 18 },
    },
  };

  return (
    <>
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{ backgroundImage: "url(/assets/images/backgrounds/Annual.jpg)" }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <div className="container">
          <div className="page-header__inner">
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              Annual Reports
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "24px", marginTop: "20px" }}
            >
              Discover Vikash's annual reports for a comprehensive look at our
              yearly progress, impact, and commitment to community development
              and sustainability.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Annual Reports Section */}
      <Container className="my-5 text-center">
        <Row className="mb-4">
          <div className="col">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-success mb-0"
              style={{ letterSpacing: "0.1em" }}
            >
              __Our Annual Reports__
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="fw-bold fs-1"
            >
              Are You Ready For a Better Our Active Services.
            </motion.h2>
          </div>
        </Row>

        {loading ? (
          <div className="text-center py-5">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-5">No annual reports found.</div>
        ) : (
          <motion.div
            className="annual-reports-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {reports.map((report) => {
              const title = getReportTitle(report);
              const pdfUrl = resolvePdfUrl(report);
              return (
                <motion.div
                  key={report.id}
                  variants={itemVariants}
                >
                  <PdfPreviewCard pdfUrl={pdfUrl} title={title} />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </Container>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-target__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default AnnualReports;
